import { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { apiFetch, setStoredToken } from "./lib/apiFetch";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DemoPage from "./pages/DemoPage";
import DemoLivePage from "./pages/DemoLivePage";
import ContactPage from "./pages/ContactPage";
import { LegalNoticePage, PrivacyPolicyPage, TermsOfServicePage } from "./pages/LegalPages";
import AuthCallback from "./pages/AuthCallback";
import OnboardingPage from "./pages/OnboardingPage";
import ConnectOutlookPage from "./pages/ConnectOutlookPage";
import ROICalculatorPage from "./pages/ROICalculatorPage";
import DashboardLayout from "./layouts/DashboardLayout";
import OverviewPage from "./pages/dashboard/OverviewPage";
import AnalyticsPage from "./pages/dashboard/AnalyticsPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import InboxPage from "./pages/dashboard/InboxPage";
import DashboardSelectPage from "./pages/dashboard/DashboardSelectPage";
import NotFoundDashboard from "./pages/dashboard/NotFoundDashboard";
import PricingPage from "./pages/PricingPage";
import ProjectsListPage from "./pages/dashboard/ProjectsListPage";
import NewProjectPage from "./pages/dashboard/NewProjectPage";
import ProjectDetailPage from "./pages/dashboard/ProjectDetailPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Theme Context
export const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("alia-theme");
      return saved || "dark";
    }
    return "dark";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("alia-theme", theme);
      const root = document.documentElement;
      if (root) {
        if (theme === "dark") {
          root.classList.add("dark");
        } else {
          root.classList.remove("dark");
        }
      }
    }
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      try {
        const response = await apiFetch(`${API}/auth/me`);
        if (response.ok && isMounted) {
          const userData = await response.json();
          setUser(userData);
        } else if (isMounted) {
          setUser(null);
          if (response.status === 401) setStoredToken("");
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    if (token) setStoredToken(token);
  };

  const logout = async () => {
    try {
      await apiFetch(`${API}/auth/logout`, { method: "POST" });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setStoredToken("");
  };

  const checkAuth = async () => {
    try {
      const response = await apiFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        if (response.status === 401) setStoredToken("");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    // Wait for auth check to finish before deciding anything.
    if (loading) return;

    // No user — no need to check subscription.
    if (!user) {
      setSubscriptionStatus(null);
      setCheckingSubscription(false);
      return;
    }

    // User is present — set checking=true BEFORE async so the loader stays visible
    // during the in-flight subscription lookup (fixes the race where the redirect
    // fired before the check completed).
    setCheckingSubscription(true);
    let cancelled = false;

    (async () => {
      try {
        const response = await apiFetch(`${API}/billing/subscription-status`);
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setSubscriptionStatus(data);
        } else if (!cancelled) {
          setSubscriptionStatus({ subscription_status: "none", outlook_connected: false });
        }
      } catch (error) {
        console.error("Subscription check failed:", error);
        if (!cancelled) setSubscriptionStatus({ subscription_status: "none", outlook_connected: false });
      } finally {
        if (!cancelled) setCheckingSubscription(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, loading]);

  // Show loader while: auth is loading, OR subscription check is in-flight, OR
  // (user is logged in but subscriptionStatus hasn't been resolved yet on a dashboard route).
  const needsSubscriptionStatus =
    !!user && location.pathname.startsWith("/dashboard") && subscriptionStatus === null;

  if (loading || checkingSubscription || needsSubscriptionStatus) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check onboarding status
  if (!user.onboarding_completed && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  // For dashboard routes, check subscription and Outlook connection
  if (location.pathname.startsWith("/dashboard")) {
    const hasValidSubscription = subscriptionStatus?.subscription_status === "trialing" || 
                                  subscriptionStatus?.subscription_status === "active";
    const hasOutlook = subscriptionStatus?.outlook_connected;
    
    // No subscription -> pricing page
    if (!hasValidSubscription) {
      return <Navigate to="/pricing" replace />;
    }
    
    // Has subscription but no Outlook -> connect page
    if (!hasOutlook) {
      return <Navigate to="/connect-outlook" replace />;
    }
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/demo-live" element={<DemoLivePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/roi" element={<ROICalculatorPage />} />
      
      {/* Legal pages */}
      <Route path="/legal" element={<LegalNoticePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      
      {/* Protected routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/connect-outlook" element={
        <ProtectedRoute>
          <ConnectOutlookPage />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardSelectPage />
        </ProtectedRoute>
      } />
      <Route element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard/inbox" element={<InboxPage />} />
        <Route path="/dashboard/inbox/:emailId" element={<InboxPage />} />
        <Route path="/dashboard/analytics" element={<AnalyticsPage />} />
        <Route path="/dashboard/settings" element={<SettingsPage />} />
        <Route path="/dashboard/projects" element={<ProjectsListPage />} />
        <Route path="/dashboard/projects/overview" element={<OverviewPage />} />
        <Route path="/dashboard/projects/new" element={<NewProjectPage />} />
        <Route path="/dashboard/projects/:projectId" element={<ProjectDetailPage />} />
      </Route>
      {/* Dedicated 404 for /dashboard/* to avoid silent fallback to "/" */}
      <Route path="/dashboard/*" element={
        <ProtectedRoute>
          <NotFoundDashboard />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
